/** Safari keeps toolbars expanded when the visible height is at least this much short of the collapsed height. */
export const TOOLBAR_THRESHOLD_PX = 20

/** Delay before re-locking document scroll after toolbars collapse, so scroll momentum can settle. */
export const SCROLL_LOCK_DELAY_MS = 100

/** The on-screen keyboard hides at least this much of the layout viewport; toolbars never open such a gap. */
export const KEYBOARD_THRESHOLD_PX = 100

/** Visual viewport scale above which the page counts as pinch-zoomed. */
const PINCH_ZOOM_SCALE = 1.01

type SwipeFullscreenEligibilityInput = {
  enableFull3d: boolean
  device: string
  isMobileApp: boolean
  osName: string | undefined
  isEmbedded: boolean
  isStandalone: boolean
  /** In-app webviews (Instagram, LINE, Facebook…) keep their own chrome, so a swipe never collapses it. */
  isInAppBrowser: boolean
}

export function isSwipeFullscreenEligible({
  enableFull3d,
  device,
  isMobileApp,
  osName,
  isEmbedded,
  isStandalone,
  isInAppBrowser,
}: SwipeFullscreenEligibilityInput): boolean {
  return (
    enableFull3d &&
    device === 'mobile_browser' &&
    !isMobileApp &&
    osName === 'iOS' &&
    !isEmbedded &&
    !isStandalone &&
    !isInAppBrowser
  )
}

type ToolbarMeasurement = {
  /** Visible viewport height including its offset (`visualViewport.height + offsetTop`). */
  visibleHeight: number
  /** `screen.width` / `screen.height`; iOS keeps them in portrait order regardless of rotation. */
  screenWidth: number
  screenHeight: number
}

/** Landscape only: toolbars are expanded while the visible height is short of the screen's short side (Playgama rule). */
export function areToolbarsExpanded({
  visibleHeight,
  screenWidth,
  screenHeight,
}: ToolbarMeasurement): boolean {
  const collapsedHeight = Math.min(screenWidth, screenHeight)
  if (collapsedHeight <= 0) return false
  return visibleHeight < collapsedHeight - TOOLBAR_THRESHOLD_PX
}

type ViewportOverlayMeasurement = {
  /** `visualViewport.scale` */
  scale: number
  /** `visualViewport.height` */
  visualHeight: number
  /** `window.innerHeight`; the keyboard leaves it unchanged, toolbars shrink it with the visual viewport. */
  layoutHeight: number
}

/** True while pinch-zoom or the on-screen keyboard shrinks the visual viewport, which says nothing about toolbars. */
export function isViewportOverlaid({
  scale,
  visualHeight,
  layoutHeight,
}: ViewportOverlayMeasurement): boolean {
  return scale > PINCH_ZOOM_SCALE || layoutHeight - visualHeight > KEYBOARD_THRESHOLD_PX
}

type ScrollRoomMeasurement = {
  scrollHeight: number
  viewportHeight: number
  scrollY: number
}

/** True when less than one viewport of document scroll remains, so another swipe-up cannot collapse the toolbars. */
export function needsScrollRoomReset({
  scrollHeight,
  viewportHeight,
  scrollY,
}: ScrollRoomMeasurement): boolean {
  return scrollHeight - viewportHeight - scrollY < viewportHeight
}

export function isStandaloneDisplayMode(): boolean {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean }
  return (
    navigatorWithStandalone.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}
